/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { MutableRef } from 'preact/hooks';
import { keyExtractor } from '../../utils/PRIVATE_collectionUtils';
import { Keys, containsKey, isSameKey, addKey, removeKey } from '../../utils/UNSAFE_keys';
import { SizingInfo, TableColumn } from '../Table';
import {
  logicalCellExtractor,
  getCellType,
  getRowKeyForRowIndex,
  getRowIndexForRowElement,
  getElementFromCell,
  getColumnIndexForHeaderFooterElement
} from './TableDomUtils';
import { applyScrollDetail } from './TableScrollUtils';
import { SelectionMode } from '../../UNSAFE_Collection';
import { Cell, TableSelectionDetail } from '../../UNSAFE_TableView';

/**
 * Helper function to handle a selection gesture (click, spacebar, etc...)
 */
export function handleSelectionGesture<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  target: HTMLElement,
  isMultiSelectGesture: boolean,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  selectionMode?: { row?: SelectionMode; column?: SelectionMode },
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selected?.row != null ? selected.row : (emptyKeys as Keys<K>);
  let selectedColumns = selected?.column != null ? selected.column : (emptyKeys as Keys<C>);

  const newRowKey = keyExtractor(target, `[data-oj-table-data-row='${tableId}']`) as K;
  if (newRowKey != null) {
    if (selectionMode?.row === 'single' || selectionMode?.row === 'multiple') {
      if (selectedRows == null) {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      } else if (!containsKey(selectedRows, newRowKey)) {
        if (isMultiSelectGesture && selectionMode.row === 'multiple') {
          selectedRows = addKey(selectedRows, newRowKey);
        } else {
          selectedRows = { all: false, keys: new Set([newRowKey]) };
        }
      } else if (isMultiSelectGesture) {
        selectedRows = removeKey(selectedRows, newRowKey, false);
      } else {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      }
      selectedColumns = emptyKeys as Keys<C>;
    }
  } else if (selectionMode?.column === 'single' || selectionMode?.column === 'multiple') {
    const cellElement = target.closest(`[data-oj-table-focusable='${tableId}']`) as HTMLElement;
    if (cellElement != null) {
      const cellType = getCellType(cellElement);
      if (cellType === 'header' || cellType === 'footer') {
        const index = getColumnIndexForHeaderFooterElement(
          root,
          tableId,
          cellElement,
          cellType === 'header'
        );
        if (index != null) {
          const column = columnsArray[index];
          if (column.value.selectable !== 'disabled') {
            if (selectedColumns == null) {
              selectedColumns = { all: false, keys: new Set([column.key]) };
            } else if (!containsKey(selectedColumns, column.key)) {
              if (isMultiSelectGesture && selectionMode.column === 'multiple') {
                selectedColumns = addKey(selectedColumns, column.key);
              } else {
                selectedColumns = { all: false, keys: new Set([column.key]) };
              }
            } else if (isMultiSelectGesture) {
              selectedColumns = removeKey(selectedColumns, column.key, false);
            } else {
              selectedColumns = { all: false, keys: new Set([column.key]) };
            }
            selectedRows = emptyKeys as Keys<K>;
          }
        }
      }
    }
  }

  if (!isSameKey(selectedRows, selected?.row) || !isSameKey(selectedColumns, selected?.column)) {
    onSelectionChange(
      { value: { row: selectedRows, column: selectedColumns }, target: target },
      false
    );
  }
}

/**
 * Helper function to calculate and apply a new selection state corresponding to the range provided
 */
function _applyRangeSelection<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  target: HTMLElement,
  startIndex: number,
  endIndex: number,
  isRowGesture: boolean,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  selectionMode?: { row?: SelectionMode; column?: SelectionMode },
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selected?.row != null ? selected.row : (emptyKeys as Keys<K>);
  let selectedColumns = selected?.column != null ? selected.column : (emptyKeys as Keys<C>);

  if (isRowGesture) {
    selectedColumns = emptyKeys as Keys<C>;
    if (selectionMode?.row === 'multiple') {
      selectedRows = emptyKeys as Keys<K>;
      // selected rows should be added in the order that they are selected
      if (startIndex <= endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
          const key = getRowKeyForRowIndex(root, tableId, i) as K;
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
      } else {
        for (let i = startIndex; i >= endIndex; i--) {
          const key = getRowKeyForRowIndex(root, tableId, i) as K;
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
      }
    }
  } else {
    selectedRows = emptyKeys as Keys<K>;
    if (selectionMode?.column === 'multiple') {
      selectedColumns = emptyKeys as Keys<C>;
      // selected columns should be added in the order that they are selected
      if (startIndex <= endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
          const column = columnsArray[i];
          if (column.value.selectable !== 'disabled') {
            selectedColumns = addKey(selectedColumns, column.key);
          }
        }
      } else {
        for (let i = startIndex; i >= endIndex; i--) {
          const column = columnsArray[i];
          if (column.value.selectable !== 'disabled') {
            selectedColumns = addKey(selectedColumns, column.key);
          }
        }
      }
    }
  }

  if (!isSameKey(selectedRows, selected?.row) || !isSameKey(selectedColumns, selected?.column)) {
    onSelectionChange(
      { value: { row: selectedRows, column: selectedColumns }, target: target },
      true
    );
  }
}

/**
 * Determines whether a potential range selection gesture is valid based on the given target
 */
export function isRangeSelectionGesture<K, C>(
  root: HTMLDivElement,
  tableId: string,
  target: HTMLElement,
  currentCell: Cell<K, C>,
  selectionMode?: { row?: SelectionMode; column?: SelectionMode }
) {
  const targetCell = logicalCellExtractor(
    target,
    tableId,
    `[data-oj-table-focusable='${tableId}']`
  ) as Cell<K, C>;
  if (
    ((selectionMode?.row === 'multiple' && currentCell.type === 'data') ||
      (selectionMode?.column === 'multiple' &&
        (currentCell.type === 'header' || currentCell.type === 'footer'))) &&
    targetCell?.type === currentCell.type
  ) {
    const currentCellElement = getElementFromCell(root, tableId, currentCell);
    const targetCellElement = getElementFromCell(root, tableId, targetCell);
    if (currentCellElement != null && targetCellElement != null) {
      const isRow = targetCell?.type === 'data';
      if (isRow) {
        return (
          getRowIndexForRowElement(
            root,
            tableId,
            currentCellElement.parentElement as HTMLElement
          ) != null &&
          getRowIndexForRowElement(root, tableId, targetCellElement.parentElement as HTMLElement) !=
            null
        );
      } else if (targetCell?.type === 'header' || targetCell?.type === 'footer') {
        const isHeader = targetCell?.type === 'header';
        return (
          getColumnIndexForHeaderFooterElement(root, tableId, currentCellElement, isHeader) !=
            null &&
          getColumnIndexForHeaderFooterElement(root, tableId, targetCellElement, isHeader) != null
        );
      }
    }
  }
  return false;
}

/**
 * Helper function to handle a range selection gesture (shift+click)
 */
export function handleRangeSelectionGesture<K, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  target: HTMLElement,
  currentCell: Cell<K, C>,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  selectionMode?: { row?: SelectionMode; column?: SelectionMode },
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const targetCell = logicalCellExtractor(
    target,
    tableId,
    `[data-oj-table-focusable='${tableId}']`
  ) as Cell<K, C>;
  const currentCellElement = getElementFromCell(root, tableId, currentCell);
  const targetCellElement = getElementFromCell(root, tableId, targetCell);
  if (currentCellElement != null && targetCellElement != null) {
    if (
      ((selectionMode?.row === 'multiple' && targetCell?.type === 'data') ||
        (selectionMode?.column === 'multiple' &&
          (targetCell?.type === 'header' || targetCell?.type === 'footer'))) &&
      targetCell?.type === currentCell.type
    ) {
      let startIndex;
      let endIndex;
      let endElement;
      const isRow = targetCell?.type === 'data';
      if (isRow) {
        endElement = targetCellElement.parentElement as HTMLElement;
        startIndex = getRowIndexForRowElement(
          root,
          tableId,
          currentCellElement.parentElement as HTMLElement
        );
        endIndex = getRowIndexForRowElement(root, tableId, endElement);
      } else if (targetCell?.type === 'header' || targetCell?.type === 'footer') {
        const isHeader = targetCell?.type === 'header';
        endElement = targetCellElement;
        startIndex = getColumnIndexForHeaderFooterElement(
          root,
          tableId,
          currentCellElement,
          isHeader
        );
        endIndex = getColumnIndexForHeaderFooterElement(root, tableId, targetCellElement, isHeader);
      }
      if (startIndex != null && endIndex != null) {
        selectionExtensionElementRef.current = endElement;
        _applyRangeSelection(
          root,
          tableId,
          columnsArray,
          target,
          startIndex,
          endIndex,
          isRow,
          onSelectionChange,
          selectionMode,
          selected
        );
        return true;
      }
    }
  }
  return false;
}

/**
 * Helper function to handle a selection extension gesture (shift+arrow key)
 */
export function handleExtendSelectionGesture<K extends string | number, D, C>(
  root: HTMLDivElement,
  tableId: string,
  columnsArray: TableColumn<K, D, C>[],
  sizingInfoRef: MutableRef<SizingInfo>,
  target: HTMLElement,
  isPrevious: boolean,
  currentCell: Cell<K, C>,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void,
  isRtl: boolean,
  fullColumnWidths?: number[],
  selectionMode?: { row?: SelectionMode; column?: SelectionMode },
  selected?: { row?: Keys<K>; column?: Keys<C> }
) {
  const currentCellElement = getElementFromCell(root, tableId, currentCell);
  if (currentCellElement != null) {
    let startIndex;
    let startElement;
    const isRow = currentCell.type === 'data';
    const isHeader = currentCell.type === 'header';
    if (isRow) {
      startElement = currentCellElement.parentElement as HTMLElement;
      startIndex = getRowIndexForRowElement(root, tableId, startElement);
    } else {
      startElement = currentCellElement;
      startIndex = getColumnIndexForHeaderFooterElement(root, tableId, startElement, isHeader);
    }
    if (startIndex != null) {
      let endIndex;
      let extendedElement;
      const cursorElement = selectionExtensionElementRef.current;
      if (cursorElement != null) {
        extendedElement = isPrevious
          ? (cursorElement.previousElementSibling as HTMLElement)
          : (cursorElement.nextElementSibling as HTMLElement);
        if (extendedElement != null) {
          selectionExtensionElementRef.current = extendedElement;
          endIndex = isRow
            ? getRowIndexForRowElement(root, tableId, extendedElement)
            : getColumnIndexForHeaderFooterElement(root, tableId, extendedElement, isHeader);
        } else {
          endIndex = isRow
            ? getRowIndexForRowElement(root, tableId, cursorElement)
            : getColumnIndexForHeaderFooterElement(root, tableId, cursorElement, isHeader);
        }
      } else {
        extendedElement = isPrevious
          ? (startElement.previousElementSibling as HTMLElement)
          : (startElement.nextElementSibling as HTMLElement);
        if (extendedElement != null) {
          selectionExtensionElementRef.current = extendedElement;
          endIndex = isRow
            ? getRowIndexForRowElement(root, tableId, extendedElement)
            : getColumnIndexForHeaderFooterElement(root, tableId, extendedElement, isHeader);
        } else {
          endIndex = startIndex;
        }
      }
      if (endIndex != null) {
        _applyRangeSelection(
          root,
          tableId,
          columnsArray,
          target,
          startIndex,
          endIndex,
          isRow,
          onSelectionChange,
          selectionMode,
          selected
        );
      }

      // scroll 'extensionElement' into view
      const extensionElement =
        selectionExtensionElementRef.current != null
          ? selectionExtensionElementRef.current
          : currentCellElement;
      let extensionCell;
      if (isRow) {
        const rowKey = keyExtractor(extensionElement, `[data-oj-table-data-row='${tableId}']`) as K;
        extensionCell = {
          type: currentCell.type,
          rowKey: rowKey,
          columnKey: currentCell.columnKey
        };
      } else {
        extensionCell = logicalCellExtractor(
          extensionElement,
          tableId,
          `[data-oj-table-focusable='${tableId}']`
        ) as Cell<K, C>;
      }
      applyScrollDetail(
        root,
        tableId,
        columnsArray,
        sizingInfoRef.current,
        { cell: extensionCell, locationX: 'inView', locationY: 'inView' },
        isRtl,
        fullColumnWidths
      );
    }
  }
}
